/**
 * Actions du menu partagées par les écrans : requête au hall avec retour visuel (toast),
 * libellé et disponibilité du bouton « Rejoindre », invitations et liens.
 */
import type { LobbyRequest, LobbyResults, ServerSummary } from '@snowchain/shared';
import { toast } from '../hud/ui-state';
import { lobby } from './lobby';
import { menu, menuActions } from './state';
import { copyText, inviteLink } from './ui';

/** Envoie une requête ; en cas de refus, toast avec la raison du serveur et null. */
export async function act<K extends LobbyRequest['op']>(req: Extract<LobbyRequest, { op: K }>, okTitle?: string, okText?: string): Promise<LobbyResults[K] | null> {
  try {
    const r = await lobby.req(req);
    if (okTitle) toast('ok', okTitle, okText);
    return r;
  } catch (e) {
    toast('danger', 'Action impossible', e instanceof Error ? e.message : String(e));
    return null;
  }
}

export interface JoinState {
  label: string;
  /** Raison d'un bouton désactivé. */
  blocked: string | null;
}

export function joinState(s: ServerSummary): JoinState {
  const meId = lobby.me.value?.id;
  const here = s.online.some((p) => p.id === meId);
  const manager = s.myRole === 'station' || (!!meId && s.owner?.id === meId);
  if (!manager && !here && s.online.length >= s.maxPlayers) return { label: 'Complet', blocked: `Serveur complet (${s.online.length}/${s.maxPlayers} joueurs)` };
  if (manager) return { label: 'Gérer ma station', blocked: null };
  if (!s.owner) return { label: 'Prendre la station', blocked: null };
  return { label: s.myRole ? 'Revenir observer' : 'Rejoindre', blocked: null };
}

export function join(s: ServerSummary | string): void {
  const id = typeof s === 'string' ? s : s.id;
  if (typeof s !== 'string') {
    const st = joinState(s);
    if (st.blocked) {
      toast('warn', 'Impossible de rejoindre', st.blocked);
      return;
    }
  }
  menu.joining.value = id;
  menuActions.join(id);
}

export async function copyInvite(worldId: string): Promise<void> {
  const ok = await copyText(inviteLink(worldId));
  if (ok) toast('ok', 'Lien copié', "Envoyez-le à un ami : il ouvrira directement la partie après s'être connecté.");
  else toast('warn', 'Copie impossible', `Code de la partie : ${worldId}`, 9000);
}

export async function copyCode(worldId: string): Promise<void> {
  const ok = await copyText(worldId);
  toast(ok ? 'ok' : 'warn', ok ? 'Code copié' : 'Copie impossible', ok ? `${worldId} : à saisir dans « Rejoindre avec un code ».` : `Code de la partie : ${worldId}`);
}

export async function inviteFriend(friend: { id: string; name: string }, world: { id: string; name: string }): Promise<boolean> {
  return !!(await act({ op: 'invite', to: friend.id, world: world.id }, 'Invitation envoyée', `${friend.name} est invité sur « ${world.name} ».`));
}

export async function requestFriend(name: string): Promise<boolean> {
  const r = await act({ op: 'friend.request', name });
  if (!r) return false;
  toast('ok', r.accepted ? 'Vous êtes amis' : 'Demande envoyée', r.accepted ? `${name} vous avait déjà demandé en ami.` : `${name} la verra dans son menu.`);
  return true;
}

/** Parties où l'on peut inviter : celles où l'on a joué (gérant d'abord). */
export function invitableWorlds(): ServerSummary[] {
  return (lobby.servers.value ?? [])
    .filter((s) => s.myRole)
    .sort((a, b) => (a.myRole === 'station' ? 0 : 1) - (b.myRole === 'station' ? 0 : 1) || (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));
}
